import type { Task } from '../../types';

/** Parse YYYY-MM-DD as a local date (avoids UTC month shifts). */
export function parseDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function todayIn(tz?: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'UTC' }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export function fmtDate(s?: string | null): string {
  return s ? parseDate(s).toLocaleDateString('ru-RU') : '—';
}

export function isOverdue(t: Task, tz?: string): boolean {
  return !t.is_complete && !!t.due_date && parseDate(t.due_date).getTime() < parseDate(todayIn(tz)).getTime();
}

/** Whole calendar days from today (wedding tz) until due_date; negative = overdue. */
export function daysUntilDue(dueDate: string, tz?: string): number {
  const due = parseDate(dueDate).getTime();
  const today = parseDate(todayIn(tz)).getTime();
  return Math.round((due - today) / 86400000);
}

export type TaskStatus = 'done' | 'overdue' | 'soon' | 'progress';

/** Server-side sort values: position | name | date | status; 'category'/'overdue' are client-side. */
export type SortMode = 'position' | 'name' | 'date' | 'status' | 'category' | 'overdue';
/** Server-side status values: all | completed | pending | overdue; 'soon' is client-side. */
export type StatusMode = 'all' | 'completed' | 'pending' | 'overdue' | 'soon';

export const CLIENT_SORTS: SortMode[] = ['category', 'overdue'];
export const CLIENT_STATUSES: StatusMode[] = ['soon'];

/**
 * Status priority: done > overdue > soon (due within 7 days incl. today) > progress.
 * Boundary-checked: yesterday=overdue, today/7 days=soon, 8 days=progress.
 */
export function getTaskStatus(t: Pick<Task, 'is_complete' | 'due_date'>, tz?: string): TaskStatus {
  if (t.is_complete) return 'done';
  if (!t.due_date) return 'progress';
  const days = daysUntilDue(t.due_date, tz);
  if (days < 0) return 'overdue';
  if (days <= 7) return 'soon';
  return 'progress';
}

export const STATUS_META: Record<TaskStatus, { label: string; cls: string }> = {
  done: { label: 'Выполнено', cls: 'tp-chip--done' },
  overdue: { label: 'Просрочено', cls: 'tp-chip--overdue' },
  soon: { label: 'Скоро срок', cls: 'tp-chip--soon' },
  progress: { label: 'В процессе', cls: 'tp-chip--progress' },
};

/** Deadline group order (Этап 6). One task = exactly one group; empty groups are hidden. */
export const DEADLINE_GROUP_ORDER = [
  'Просроченные',
  'Сегодня',
  'Завтра',
  'В ближайшие 3 дня',
  'В ближайшие 7 дней',
  'В ближайшие 14 дней',
  'В ближайшие 4 недели',
  'Более поздние сроки',
  'Без срока',
] as const;

export type DeadlineGroupLabel = (typeof DEADLINE_GROUP_ORDER)[number];

/** Bucket by whole days until due (wedding tz): <0, 0, 1, 2-3, 4-7, 8-14, 15-28, >28, none. */
export function deadlineGroupLabel(dueDate: string | null | undefined, tz?: string): DeadlineGroupLabel {
  if (!dueDate) return 'Без срока';
  const d = daysUntilDue(dueDate, tz);
  if (d < 0) return 'Просроченные';
  if (d === 0) return 'Сегодня';
  if (d === 1) return 'Завтра';
  if (d <= 3) return 'В ближайшие 3 дня';
  if (d <= 7) return 'В ближайшие 7 дней';
  if (d <= 14) return 'В ближайшие 14 дней';
  if (d <= 28) return 'В ближайшие 4 недели';
  return 'Более поздние сроки';
}

/** Pure, testable grouping: preserves input order inside each group. */
export function groupByDeadline<T extends { due_date?: string | null }>(tasks: T[], tz?: string): [DeadlineGroupLabel, T[]][] {
  const buckets = new Map<DeadlineGroupLabel, T[]>();
  for (const t of tasks) {
    const label = deadlineGroupLabel(t.due_date, tz);
    const arr = buckets.get(label);
    if (arr) arr.push(t);
    else buckets.set(label, [t]);
  }
  return DEADLINE_GROUP_ORDER.filter((l) => buckets.has(l)).map((l) => [l, buckets.get(l)!]);
}
